import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Fade,
  LinearProgress,
  Chip,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SecurityIcon from "@mui/icons-material/Security";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ApiIcon from "@mui/icons-material/Api";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";

import HomeFooter from "../shared/HomeFooter";
import Navigation from "../shared/Navigation";

function Roadmap() {
  React.useEffect(() => {
    document.title = "Roadmap - ShieldShot";
    window.scrollTo(0, 0);
  }, []);

  const roadmapItems = [
    {
      title: "Customer Accounts",
      description: "Save on emails, and send collections to your supporters directly through our platform for more convenience and security.",
      icon: <AccountCircleIcon />,
      status: "In Development",
      progress: 75,
      timeline: "Q3 2025",
      features: ["Direct messaging", "Account dashboards", "Purchase history", "Notification preferences"]
    },
    {
      title: "Shared Uploading",
      description: "Invite and allow another user to upload for you, such as a photographer who is providing your collections.",
      icon: <CloudUploadIcon />,
      status: "In Development",
      progress: 60,
      timeline: "Q3 2025",
      features: ["Multi-user access", "Permission controls", "Collaboration tools", "Upload notifications"]
    },
    {
      title: "ShieldShot Store",
      description: "Link your collections to store pages to sell to your supporters directly on ShieldShot.",
      icon: <StorefrontIcon />,
      status: "Planning",
      progress: 30,
      timeline: "Q3 2025",
      features: ["Integrated payments", "Product catalogs", "Discount codes", "Sales analytics"]
    },
    {
      title: "Download Control",
      description: "Control your customer downloads, or even disable downloading completely via view-only links for added peace of mind.",
      icon: <SecurityIcon />,
      status: "Planning",
      progress: 20,
      timeline: "Q3 2025",
      features: ["Download limits", "Time-based access", "Geographic restrictions", "Device limits"]
    },
    {
      title: "Advanced Analytics",
      description: "Comprehensive analytics dashboard with detailed insights into your content performance and audience behavior.",
      icon: <AnalyticsIcon />,
      status: "Research",
      progress: 10,
      timeline: "Q4 2025",
      features: ["Revenue tracking", "Audience insights", "Performance metrics", "Custom reports"]
    },
    {
      title: "API & Integrations",
      description: "Full API access and integrations with popular platforms like OnlyFans, Patreon, and social media platforms.",
      icon: <ApiIcon />,
      status: "Research",
      progress: 5,
      timeline: "Q4 2025",
      features: ["REST API", "Webhooks", "Platform integrations", "Third-party apps"]
    },
    {
      title: "Mobile Applications",
      description: "Native iOS and Android apps for uploading, managing, and distributing content on the go.",
      icon: <PhoneAndroidIcon />,
      status: "Future",
      progress: 0,
      timeline: "2026",
      features: ["Native apps", "Offline access", "Push notifications", "Mobile optimization"]
    },
    {
      title: "Team Collaboration",
      description: "Advanced team features for agencies and businesses managing multiple creators and content workflows.",
      icon: <GroupWorkIcon />,
      status: "Future",
      progress: 0,
      timeline: "2026",
      features: ["Team workspaces", "Role management", "Workflow automation", "Bulk operations"]
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "In Development": return "#4caf50";
      case "Planning": return "#ff9800";
      case "Research": return "#2196f3";
      case "Future": return "#9e9e9e";
      default: return "#9e9e9e";
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <Navigation />
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #2997ff 0%, #1976d2 100%)",
          py: { xs: 8, md: 12 },
          color: "white",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                color: "white",
              }}
            >
              Product Roadmap
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 400,
                maxWidth: 700,
                mx: "auto",
                opacity: 0.9,
                fontSize: { xs: "1.2rem", md: "1.5rem" },
                color: "white",
              }}
            >
              Exciting features in development to enhance your content protection and distribution experience
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Roadmap Timeline */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {roadmapItems.map((item, index) => (
            <Grid item xs={12} md={6} lg={4} key={item.title}>
              <Fade in timeout={1000 + index * 200}>
                <Card
                  sx={{
                    height: "100%",
                    p: { xs: 3, md: 4 },
                    borderRadius: 4,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease-in-out",
                    position: "relative",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  {/* Status Badge */}
                  <Chip
                    label={item.status}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      backgroundColor: getStatusColor(item.status),
                      color: "white",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  />

                  {/* Icon */}
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      backgroundColor: "rgba(41, 151, 255, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      "& svg": {
                        fontSize: "2rem",
                        color: "#2997ff",
                      },
                    }}
                  >
                    {item.icon}
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#2997ff",
                      fontWeight: 700,
                      mb: 2,
                      fontSize: { xs: "1.3rem", md: "1.5rem" },
                      pr: 2, // Space for status badge
                    }}
                  >
                    {item.title}
                  </Typography>

                  {/* Timeline */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "grey.600",
                      fontWeight: 600,
                      mb: 2,
                      fontSize: "0.9rem",
                    }}
                  >
                    Expected: {item.timeline}
                  </Typography>

                  {/* Progress Bar */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" sx={{ color: "grey.600", fontSize: "0.8rem" }}>
                        Progress
                      </Typography>
                      <Typography variant="body2" sx={{ color: "grey.600", fontSize: "0.8rem" }}>
                        {item.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={item.progress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "grey.200",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: getStatusColor(item.status),
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "grey.700",
                      lineHeight: 1.5,
                      mb: 3,
                      fontSize: { xs: "0.9rem", md: "1rem" },
                    }}
                  >
                    {item.description}
                  </Typography>

                  {/* Features List */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#2997ff",
                        fontWeight: 600,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Key Features:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {item.features.map((feature, featureIndex) => (
                        <Chip
                          key={featureIndex}
                          label={feature}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(41, 151, 255, 0.1)",
                            color: "#2997ff",
                            fontSize: "0.7rem",
                            height: "auto",
                            py: 0.5,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box sx={{ mt: { xs: 8, md: 12 }, textAlign: "center" }}>
          <Card
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              background: "linear-gradient(135deg, #2997ff 0%, #1976d2 100%)",
              color: "white",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
                color: "white",
              }}
            >
              Have a Feature Request?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                mb: 3,
                opacity: 0.9,
                maxWidth: 600,
                mx: "auto",
                color: "white",
              }}
            >
              We're always listening to our community. Share your ideas and help shape the future of ShieldShot.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontStyle: "italic",
                opacity: 0.8,
              }}
            >
              Much more is already in development, and we will continue to release new features as soon as they're ready.
            </Typography>
          </Card>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ mt: 0, backgroundColor: "#f1f1f1", py: 0 }}>
        <HomeFooter />
      </Box>
    </Box>
  );
}

export default Roadmap;
